import React, { useCallback, useMemo } from 'react';
import {
  ButtonGroup,
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { tableColumns } from './table.columns';
import SkeletonRow from '@/components/data-table/SkeletonRow';
import { AdminTableInfoDto } from '@repo/common-types';
import routes from '@/routes/routes-enums';
import SortableTableHeader from '@/components/data-table/SortableTableHeader';

interface TablesTableProps {
  data: AdminTableInfoDto[];
  isLoading: boolean;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
}

const TablesTable: React.FC<TablesTableProps> = ({ data, isLoading, sorting, onSortingChange }) => {
  const navigate = useNavigate();

  const columns = useMemo(() => tableColumns(navigate), [navigate]);

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      onSortingChange(newSorting);
    },
    [sorting, onSortingChange],
  );

  const tableInstance = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
  });

  return (
    <Card className="card mb-2">
      <CardBody className="overflow-auto">
        <table className="table-hover table-centered table-nowrap mb-0 table align-middle">
          <thead>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <SortableTableHeader key={header.id} header={header} />
                ))}
                <th>Actions</th>
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading
              ? [...Array(5)].map((_, index) => <SkeletonRow key={index} columns={columns} />)
              : tableInstance.getRowModel().rows.map((row) => (
                  <tr key={row.original.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          maxWidth: `${cell.column.getSize()}px`,
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                    <td>
                      <ButtonGroup>
                        <UncontrolledDropdown>
                          <DropdownToggle tag="button" className="btn btn-soft-primary">
                            <i className="mdi mdi-chevron-down"></i>
                          </DropdownToggle>
                          <DropdownMenu className="dropdownmenu-primary">
                            <DropdownItem
                              onClick={() => {
                                const url = `${process.env.REACT_APP_PHENOM_APP}/table/${row.original.id}`;
                                const width = 1024;
                                const height = 768;
                                window.open(url, row.original.id, `height=${height},width=${width}`);
                              }}
                            >
                              Open
                            </DropdownItem>
                            <DropdownItem onClick={() => navigate(`${routes.TABLE}/${row.original.id}`)}>
                              Details
                            </DropdownItem>
                            <DropdownItem onClick={() => navigate(`${routes.TABLE_EDIT}/${row.original.id}`)}>
                              Edit
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </ButtonGroup>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </CardBody>
    </Card>
  );
};

export default TablesTable;
